import { Show } from '../types/Show';
import { debugLog } from './debugLog';
import { recordImageLoadMetric } from '../services/imageCacheService';
import { forwardImagePerformanceAggregate } from '../services/imagePerformanceAnalytics';

export type ImageSize = 'small' | 'medium' | 'large';

export interface ImagePerformanceMetrics {
  loadTime: number;
  size: ImageSize;
  isMobile: boolean;
  cacheHit: boolean;
}

const DIMENSION_REGEX = /(?<width>\d+)x(?<height>\d+)/;

export interface ImageVariantDetail {
  key: string;
  url: string;
  width: number | null;
  height: number | null;
  dimensionSource: 'key' | 'url' | 'unknown';
}

const extractDimensions = (key: string, url: string): [number | null, number | null, 'key' | 'url' | 'unknown'] => {
  const keyMatch = key.match(DIMENSION_REGEX);
  if (keyMatch?.groups) {
    return [Number(keyMatch.groups.width), Number(keyMatch.groups.height), 'key'];
  }

  const urlMatch = url.match(DIMENSION_REGEX);
  if (urlMatch?.groups) {
    return [Number(urlMatch.groups.width), Number(urlMatch.groups.height), 'url'];
  }

  return [null, null, 'unknown'];
};

export const getImageVariantDetails = (images: Show['images']): ImageVariantDetail[] => {
  if (!images) {
    return [];
  }

  const entries = Object.entries(images)
    .filter(([, url]) => Boolean(url))
    .map(([key, url]) => {
      const optimizedUrl = optimizeImageUrl(url as string);
      const [width, height, dimensionSource] = extractDimensions(key, optimizedUrl);
      return {
        key,
        url: optimizedUrl,
        width,
        height,
        dimensionSource
      };
    });

  const withDimensions = entries
    .filter((entry) => entry.width !== null && entry.height !== null)
    .sort((a, b) => (b.width! - a.width!) || (b.height! - a.height!));

  const withoutDimensions = entries.filter((entry) => entry.width === null || entry.height === null);

  return [...withDimensions, ...withoutDimensions];
};

const IMAGE_TARGET_WIDTH: Record<ImageSize, { mobile: number; desktop: number }> = {
  small: { mobile: 260, desktop: 320 },
  medium: { mobile: 320, desktop: 360 },
  large: { mobile: 360, desktop: 420 }
};

const pickBestVariant = (
  variants: ImageVariantDetail[],
  targetWidth: number
): ImageVariantDetail | undefined => {
  const withDimensions = variants.filter((variant) => variant.width !== null);

  if (withDimensions.length) {
    return withDimensions.reduce<ImageVariantDetail | undefined>((best, current) => {
      if (!best) {
        return current;
      }

      const bestDiff = Math.abs((best.width ?? targetWidth) - targetWidth);
      const currentDiff = Math.abs((current.width ?? targetWidth) - targetWidth);

      if (currentDiff < bestDiff) {
        return current;
      }

      if (currentDiff === bestDiff) {
        // Prefer the smaller asset when differences match to save bandwidth.
        return (current.width ?? Infinity) < (best.width ?? Infinity) ? current : best;
      }

      return best;
    }, undefined);
  }

  return variants[0];
};

export const getOptimalImageSize = (
  images: Show['images'],
  size: ImageSize,
  isMobile: boolean = false
): string | undefined => {
  const variants = getImageVariantDetails(images);
  if (!variants.length) {
    return undefined;
  }

  const targets = IMAGE_TARGET_WIDTH[size] ?? IMAGE_TARGET_WIDTH.medium;
  const targetWidth = isMobile ? targets.mobile : targets.desktop;

  const selectedVariant = pickBestVariant(variants, targetWidth) ?? variants[0];

  return selectedVariant?.url ? optimizeImageUrl(selectedVariant.url) : undefined;
};

export const getResponsiveImageSizes = (images: Show['images'], isMobile: boolean = false) => {
  return {
    small: getOptimalImageSize(images, 'small', isMobile),
    medium: getOptimalImageSize(images, 'medium', isMobile),
    large: getOptimalImageSize(images, 'large', isMobile)
  };
};

export const preloadCriticalImages = (shows: Show[], count: number = 5): string[] => {
  const isMobile = isMobileDevice();
  
  return shows.slice(0, count)
    .map(show => getOptimalImageSize(show.images, isMobile ? 'medium' : 'large', isMobile))
    .filter(Boolean) as string[];
};

export const preloadCommonPresenterImages = (shows: Show[], count: number = 10): string[] => {
  const isMobile = isMobileDevice();
  const presenterImages = new Map<string, string>();
  
  // Get unique presenter images
  shows.forEach(show => {
    if (show.presenter?.name && !presenterImages.has(show.presenter.name)) {
      const imageUrl = getOptimalImageSize(show.images, 'medium', isMobile);
      if (imageUrl) {
        presenterImages.set(show.presenter.name, imageUrl);
      }
    }
  });
  
  return Array.from(presenterImages.values()).slice(0, count);
};

export const isMobileDevice = (): boolean => {
  // Enhanced mobile detection
  if (typeof window === 'undefined') return false;
  
  // Check viewport width (Tailwind's sm breakpoint)
  const isMobileViewport = window.innerWidth < 768;
  
  // Check user agent for mobile devices
  const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
  
  // Check for touch capability
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  return isMobileViewport || (isMobileUserAgent && isTouchDevice);
};

export const getImageLoadPriority = (index: number, isAboveFold: boolean = false): 'high' | 'low' => {
  // First 3 images or above-the-fold images get high priority
  return (index < 3 || isAboveFold) ? 'high' : 'low';
};

export const calculateImageBandwidth = (imageUrl: string, loadTime: number): number => {
  // Estimate bandwidth based on typical image sizes
  const sizeEstimates = {
    '150x150': 15000,   // ~15KB
    '300x300': 45000,   // ~45KB
    '400x400': 80000,   // ~80KB
    '500x500': 125000,  // ~125KB
    '600x600': 180000   // ~180KB
  };
  
  // Extract size from URL if possible
  const sizeMatch = imageUrl.match(/(\d+x\d+)/);
  const estimatedSize = sizeMatch ? sizeEstimates[sizeMatch[1] as keyof typeof sizeEstimates] || 100000 : 100000;
  
  // Calculate bandwidth in bytes per second
  return loadTime > 0 ? (estimatedSize / loadTime) * 1000 : 0;
};

export const shouldUseWebP = (): boolean => {
  // Check if browser supports WebP
  if (typeof window === 'undefined') return false;
  
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  
  return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
};

const SHOPIFY_HOST_PATTERNS = [
  'cdn.shopify.com',
  'shopify.com',
  'shopifycdn.net',
  'myshopify.com'
];

const shouldSkipCdnRewrite = (host: string): boolean => {
  const normalizedHost = host.toLowerCase();
  return SHOPIFY_HOST_PATTERNS.some(pattern => normalizedHost === pattern || normalizedHost.endsWith(`.${pattern}`));
};

export const optimizeImageUrl = (originalUrl: string): string => {
  if (!originalUrl) {
    return originalUrl;
  }

  if (process.env.NODE_ENV === 'test') {
    return originalUrl;
  }

  const trimmedUrl = originalUrl.trim();
  if (trimmedUrl.startsWith('data:') || trimmedUrl.startsWith('blob:')) {
    return originalUrl;
  }

  try {
    const optimizedUrl = new URL(originalUrl);
    const cdnBase = process.env.REACT_APP_IMAGE_CDN_BASE;

    if (cdnBase) {
      const cdnUrl = new URL(cdnBase);
      if (!shouldSkipCdnRewrite(optimizedUrl.host)) {
        optimizedUrl.protocol = cdnUrl.protocol || optimizedUrl.protocol;
        optimizedUrl.host = cdnUrl.host;

        const basePath = cdnUrl.pathname.replace(/\/$/, '');
        const strippedOriginPath = optimizedUrl.pathname.replace(/^\/eruption(\/|$)/, '/');
        const existingPath = strippedOriginPath.replace(/^\//, '');

        optimizedUrl.pathname = basePath
          ? `${basePath}/${existingPath}`
          : `/${existingPath}`;
      }
    }

    if (!optimizedUrl.searchParams.has('cache')) {
      optimizedUrl.searchParams.set('cache', 'immutable');
    }

    return optimizedUrl.toString();
  } catch (error) {
    debugLog('Failed to optimize image URL', { originalUrl, error });
    return originalUrl;
  }
};

// Performance tracking utilities
export const trackImagePerformance = (metrics: ImagePerformanceMetrics) => {
  if (process.env.NODE_ENV === 'development') {
    debugLog('Image Performance:', {
      loadTime: `${metrics.loadTime}ms`,
      size: metrics.size,
      device: metrics.isMobile ? 'mobile' : 'desktop',
      cached: metrics.cacheHit ? 'yes' : 'no'
    });
  }

  recordImageLoadMetric({
    cacheHit: metrics.cacheHit,
    size: metrics.size,
    isMobile: metrics.isMobile,
    loadTime: metrics.loadTime
  });
  forwardImagePerformanceAggregate();
  
  // In production, you might send this to analytics
  // analytics.track('image_load', metrics);
};

export const getImageCacheKey = (show: Show, size: ImageSize, isMobile: boolean): string => {
  return `${show.show_id}-${size}-${isMobile ? 'mobile' : 'desktop'}`;
};

export const getDefaultSizesForImage = (size: ImageSize): string => {
  const target = IMAGE_TARGET_WIDTH[size] ?? IMAGE_TARGET_WIDTH.medium;
  return `(max-width: 768px) ${Math.round(target.mobile)}px, ${Math.round(target.desktop)}px`;
};
